import { useCallback } from 'react';

import { createMentions, MentionApiError } from '../api';
import { useMentionContext } from '../MentionProvider';
import type { CreateMentionRequest, MentionResult, MentionApiErrorData } from '../types';

export interface UseMentionApiReturn {
  createMention: (
    entityId: string,
    request: CreateMentionRequest,
  ) => Promise<MentionResult[] | null>;
}

export function useMentionApi(callbacks?: {
  onMentionCreated?: (mention: MentionResult) => void;
  onMentionError?: (error: MentionApiErrorData) => void;
}): UseMentionApiReturn {
  const { baseUrl, getAccessToken } = useMentionContext();

  const createMention = useCallback(
    async (entityId: string, request: CreateMentionRequest): Promise<MentionResult[] | null> => {
      try {
        const token = await getAccessToken();
        const response = await createMentions(baseUrl, token, entityId, request);
        response.mentions.forEach((m) => callbacks?.onMentionCreated?.(m));
        return response.mentions;
      } catch (err) {
        const errorData: MentionApiErrorData =
          err instanceof MentionApiError
            ? { code: err.code, message: err.message, details: err.details }
            : { code: 'UNKNOWN_ERROR', message: 'Failed to create mention' };
        callbacks?.onMentionError?.(errorData);
        return null;
      }
    },
    [baseUrl, getAccessToken, callbacks],
  );

  return { createMention };
}
